import { AppBar, Grid, Toolbar, Typography } from "@material-ui/core";
import CssBaseline from "@material-ui/core/CssBaseline";
import CancelIcon from "@material-ui/icons/Cancel";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useCallback, useEffect, useRef, useState } from "react";
import TagManager from "react-gtm-module";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { setParticipant } from "../../../auth/actions/login.actions";
import { Footer } from "../../../components/footer";
import { type Coupon } from "../../../models/coupon";
import { type StripeParticipant } from "../../../models/stripeParticipant";
import { Alert } from "../../../shared/components/Alert";
import { LinkKeepUTM } from "../../../shared/components/LinkKeepUTM";
import { supportEmail } from "../../../shared/consts/emails";
import { PARTICIPANT_STRIPE_HOME_PATH } from "../../../shared/consts/participantHomePath";
import { useHistory } from "../../../shared/hooks/useHistory";
import { useSearchParams } from "../../../shared/hooks/useSearchParams";
import { FruitionLogo } from "../../../shared/icons";
import { validateEmail } from "../../../shared/validateEmail";
import { type RootState } from "../../../store";
import {
  asyncGetCoupon,
  setCoupon,
  setIncorrectCoupon,
  setIsFetchingStripe,
} from "../../actions/stripeActions";
import { subscriptions } from "../../helpers/subscriptions";
import { subscriptionService } from "../../services/stripeServices";
import { OrderSummaryForm } from "../orderSummaryForm";
import { PaymentForm } from "../paymentForm";
import { useStyles } from "./subscription-form.styles";

const INITIAL_SUBSCRIPTION = subscriptions.find(
  (subscription) =>
    subscription.id === process.env.REACT_APP_STRIPE_MONTHLY_SUBSCRIPTION_ID
);

const INITIAL_ERRORS_STATE = {
  email: {
    hasErrorEmail: false,
    message: "",
  },
  name: {
    hasErrorName: false,
    message: "",
  },
  lastName: {
    hasErrorLastName: false,
    message: "",
  },
  priceId: {
    hasErrorPriceId: false,
    message: "",
  },
};

interface Selector {
  coupon: Coupon;
  couponStatus: boolean;
  stripeParticipant: StripeParticipant;
  promotionCode: string;
  participantEmail: string;
  participantProfileId: string;
  participantOrganization: RootState["auth"]["login"]["organization"];
}

interface FormState {
  email: string;
  name: string;
  lastName: string;
  mobile?: string;
  coupon?: string;
}

interface FooterOptions {
  section: string;
  path: string;
}

export const SubscriptionForm = () => {
  const classes = useStyles();
  const location = useLocation();
  const searchParams = useSearchParams();
  const isEnrollPage = location.pathname === "/enroll";
  const history = useHistory();
  const elements = useElements();
  const dispatch = useDispatch();
  const stripe = useStripe();
  const [isOpenAlert, setIsOpenAlert] = useState(false);
  const [couponInput, setCouponInput] = useState(" ");
  const [errorValues, setErrorsForm] = useState(INITIAL_ERRORS_STATE);
  const [subscription, setSubscription] = useState(INITIAL_SUBSCRIPTION);
  const [total, setTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [organization, setOrganization] = useState(100);
  const [isOpenSuccesPopup, handleCloseSuccessPopup] = useState(false);
  const [formValues, setFormValues] = useState<FormState>({
    email: "",
    name: "",
    lastName: "",
    mobile: "",
    coupon: "",
  });
  const [incompleteCard, setIncompleteCard] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(null);

  const {
    coupon,
    couponStatus,
    stripeParticipant,
    promotionCode,
    participantEmail,
    participantProfileId,
    participantOrganization,
  } = useSelector<RootState, Selector>((state) => {
    return {
      coupon: state.stripeReducer.coupon,
      couponStatus: state.stripeReducer.couponStatus,
      stripeParticipant: state.auth.login.stripeParticipant,
      promotionCode: state.auth.login.organization.promotionCode,
      participantEmail: state.profile.email,
      participantProfileId: state.profile.participantProfileId,
      participantOrganization: state.auth.login.organization,
    };
  });

  useEffect(() => {
    const id = setTimeout(() => {
      setIsOpenAlert(false);
    }, 5000);

    return () => clearTimeout(id);
  }, []);

  const redirectTo = () => {
    history.push(PARTICIPANT_STRIPE_HOME_PATH);
  };

  function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return value ? ref.current : null;
  }
  const prevPaymentMethod = usePrevious(paymentMethod);

  const onSuccessSubscription = () => {
    setErrorsForm(INITIAL_ERRORS_STATE);
    dispatch(setCoupon({}));
    dispatch(setIncorrectCoupon(true));
    handleCloseSuccessPopup((state) => !state);

    const dataLayerAnalytics = {
      js: new Date(),
      event: "purchase",
      email: participantEmail,
      participant_id: participantProfileId,
      organization_id: participantOrganization.id,
    };

    for (const [key, value] of searchParams?.entries() ?? []) {
      if (key.startsWith("utm_")) {
        dataLayerAnalytics[key] = value;
      }
    }

    if (process.env.REACT_APP_GA_ID) {
      // Google Analytics 4 custom event tracking.
      TagManager.dataLayer({ dataLayer: dataLayerAnalytics });
    }

    if (process.env.REACT_APP_GADS_CONVERSION_PURCHASE) {
      const dataLayerAds = {
        event: "conversion",
        send_to: process.env.REACT_APP_GADS_CONVERSION_PURCHASE,
        event_callback: (url?: string) => {
          if (!!url && typeof url === "string") {
            window.location.href = url;
          }
        },
      };

      // Google Ads conversion tracking.
      TagManager.dataLayer({ dataLayer: dataLayerAds });
    }
  };

  const generateStripeSubscription = useCallback(
    async (paymentMethodId) => {
      try {
        const data = {
          name: formValues.name,
          lastName: formValues.lastName,
          email: formValues.email,
          mobile: formValues.mobile,
          paymentMethodId,
          priceId: subscription.priceId,
          couponId: coupon.id,
          organizationId: location.pathname === "enroll" ? 0 : organization,
          participantProfileId: stripeParticipant.profileId,
          promotionCode: "",
        };

        if (location.pathname === "/subscription") {
          delete data.participantProfileId;
        }

        if (isEnrollPage) {
          data.promotionCode = couponInput;
        }

        const res = await subscriptionService(data, location.pathname);

        if (!coupon.fullPromotionCode) {
          const { clientSecretLatestInvoicePaymentIntent, status } =
            res.data.data.subscription;
          const { participantProfileId } = res.data.data;
          dispatch(setParticipant({ profileId: participantProfileId }));

          if (status === "requires_action") {
            stripe
              .confirmCardPayment(clientSecretLatestInvoicePaymentIntent)
              .then(function (r) {
                if (r.error) {
                  setErrorsForm({
                    ...errorValues,
                    email: { hasErrorEmail: true, message: r.error.message },
                  });
                  // Display error message in your UI.
                  // The card was declined (i.e. insufficient funds, card has expired, etc)
                } else {
                  setErrorsForm(INITIAL_ERRORS_STATE);
                  dispatch(setCoupon({}));
                  dispatch(setIncorrectCoupon(true));
                  handleCloseSuccessPopup((state) => !state);
                  // Open success popover
                }
              });
          } else {
            onSuccessSubscription();
          }
        } else {
          onSuccessSubscription();
        }
      } catch (err) {
        const message = err?.response?.data?.warnings?.shift();
        setIsLoading(false);
        if (message?.includes("participant")) {
          setErrorsForm({
            ...errorValues,
            email: {
              hasErrorEmail: true,
              message:
                "This email is already connected to a subscription, please try with different email",
            },
          });
        } else {
          history.push(`/declined/?error:${message || "Something went wrong"}`);
        }
      }
    },
    [
      coupon,
      dispatch,
      errorValues,
      formValues,
      history,
      organization,
      location.pathname,
      stripe,
      stripeParticipant.profileId,
      subscription.priceId,
    ]
  );

  useEffect(() => {
    if (paymentMethod !== prevPaymentMethod) {
      generateStripeSubscription(paymentMethod);
    }
  }, [generateStripeSubscription, paymentMethod, prevPaymentMethod]);

  useEffect(() => {
    dispatch(setIsFetchingStripe({ isFetching: false }));
    if (location.pathname === "/enroll") {
      setFormValues(stripeParticipant);
    }
    setErrorsForm(INITIAL_ERRORS_STATE);
    setIncompleteCard("");
  }, [dispatch, stripeParticipant, location.pathname]);

  useEffect(() => {
    if (stripe) {
      const pr = stripe.paymentRequest({
        country: "US",
        currency: "usd",
        total: {
          label: "Demo total",
          amount: Number(total.toString().replaceAll(".", "")),
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });
      // Is not used anymore but stayed as reference
      // pr.canMakePayment().then((result) => {
      //   if (result) {
      //     // const wallets = Object.entries(result);
      //     // const allowedWallets = wallets.filter((ele) => ele[1]);
      //     // setButtons(allowedWallets);
      //     setPaymentRequest(pr);
      //   }
      // });
      pr.on("paymentmethod", async (e) => {
        if (e.paymentMethod.id) {
          setPaymentMethod(e.paymentMethod.id);
        }
        e.complete("success");
      });
    }
  }, [stripe, total]);

  useEffect(() => {
    const off: number = +(
      (coupon.percentOff * subscription.price) /
      100
    ).toFixed(2);
    if (coupon.percentOff > 0 && couponStatus) {
      let subTotal: number = +(subscription.price - off).toFixed(2);
      subTotal = Math.max(0, subTotal);
      setTotal(subTotal);
      setOrganization(coupon.organizationId);
    } else if (coupon.amountOff > 0 && couponStatus) {
      let subTotal: number = +(subscription.price - coupon.amountOff).toFixed(
        2
      );
      subTotal = Math.max(0, subTotal);
      setOrganization(coupon.organizationId);
      setTotal(subTotal);
    } else {
      setTotal(subscription.price);
    }
  }, [coupon, subscription.price, couponStatus]);

  useEffect(() => {
    if (!couponStatus) {
      dispatch(setCoupon({}));
    }
  }, [couponStatus, dispatch]);

  const isValidStripeData = () => {
    const { name, lastName, email } = formValues;

    if (!name?.length) {
      setErrorsForm({
        ...errorValues,
        name: { hasErrorName: true, message: "First name is required" },
      });
      return false;
    }

    if (!lastName?.length) {
      setErrorsForm({
        ...errorValues,
        lastName: { hasErrorLastName: true, message: "Last name is required" },
      });
      return false;
    }
    if (!email?.length) {
      setErrorsForm({
        ...errorValues,
        email: { hasErrorEmail: true, message: "Email is required" },
      });
      return false;
    }
    if (email?.length && !validateEmail(email)) {
      setErrorsForm({
        ...errorValues,
        email: { hasErrorEmail: true, message: "Invalid Email" },
      });
      return false;
    }
    return true;
  };

  const debouncedHandleCouponChange = useDebouncedCallback(() => {
    dispatch(asyncGetCoupon(couponInput, location.pathname));
    dispatch(setIncorrectCoupon(true));
  }, 1000);

  useEffect(() => {
    if (promotionCode) {
      setCouponInput(promotionCode);
      debouncedHandleCouponChange.callback();
    }
  }, [promotionCode, debouncedHandleCouponChange]);

  const handleSubscription = (data) => {
    const subscriptionPicked = subscriptions.find(
      (element) => element.name === data
    );
    setSubscription(subscriptionPicked);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
    setErrorsForm(INITIAL_ERRORS_STATE);
  };

  const handleSubmitSub = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorsForm(INITIAL_ERRORS_STATE);
    if (!stripe || !elements) {
      return;
    }
    const card = elements.getElement(CardNumberElement);
    const expiry = elements.getElement(CardExpiryElement);
    const cvc = elements.getElement(CardCvcElement);

    if (
      (card === null || expiry === null || cvc === null) &&
      !coupon.fullPromotionCode
    ) {
      return;
    }

    if (isValidStripeData()) {
      if (isEnrollPage && coupon.fullPromotionCode) {
        generateStripeSubscription("");
      } else {
        const result = await stripe.createPaymentMethod({
          type: "card",
          card,
          billing_details: {
            email: formValues.email,
            name: formValues.name,
          },
        });
        if (result.error) {
          setIsOpenAlert(true);
          setIncompleteCard(result.error.message);
          setIsLoading(false);
        } else {
          setIncompleteCard("");
          generateStripeSubscription(result.paymentMethod.id);
        }
      }
    }
  };

  const footerOptions: FooterOptions[] = [
    {
      section: "Help",
      path: supportEmail,
    },
  ];

  return (
    <Grid className={classes.main}>
      <Grid item md={10} className={classes.content}>
        <CssBaseline />
        <AppBar className={classes.appbar} position="fixed">
          <Toolbar className={classes.toolbar}>
            <div className={classes.imageContainer}>
              <LinkKeepUTM to="/participant/dashboard">
                <FruitionLogo />
              </LinkKeepUTM>
            </div>
          </Toolbar>
        </AppBar>
        <Grid className={classes.wrapper}>
          <Grid>
            <Typography className={classes.title}>
              Upgrade your current plan
            </Typography>
          </Grid>
          <Grid className={classes.wrapperContainer}>
            <Grid className={classes.loginRightContent}>
              <div className={classes.formWrapper}>
                <PaymentForm
                  formValues={formValues}
                  incompleteCard={incompleteCard}
                  handleChange={handleChange}
                  errorValues={errorValues}
                  isFullEnrollPromotionCode={
                    coupon.fullPromotionCode && isEnrollPage
                  }
                />
              </div>
            </Grid>
            <Grid className={[classes.loginLeftContent].join(" ")}>
              <div className={classes.formWrapper}>
                <OrderSummaryForm
                  isLoading={isLoading}
                  coupon={coupon}
                  total={total}
                  subscription={subscription}
                  subscriptions={subscriptions}
                  couponInput={couponInput}
                  promotionCode={promotionCode}
                  couponStatus={couponStatus}
                  handleSubscription={handleSubscription}
                  setCouponInput={setCouponInput}
                  debouncedHandleCouponChange={debouncedHandleCouponChange}
                  handleSubmitSub={handleSubmitSub}
                  isOpenSuccesPopup={isOpenSuccesPopup}
                  handleCloseSuccessPopup={() =>
                    handleCloseSuccessPopup((state) => !state)
                  }
                  redirectTo={redirectTo}
                  isEnrollPage={isEnrollPage}
                />
              </div>
            </Grid>
          </Grid>
        </Grid>
        <Footer className={classes.footer}>
          {footerOptions.map((option) => (
            <a
              key={option.section}
              href={option.path}
              className={classes.emailLink}
            >
              {option.section}
            </a>
          ))}
        </Footer>
      </Grid>
      <Alert
        open={isOpenAlert}
        onClose={() => setIsOpenAlert((state) => !state)}
        icon={<CancelIcon fontSize="inherit" />}
        message="Your payment couldn't be processed.
        Try again."
        severity="error"
      />
    </Grid>
  );
};

SubscriptionForm.displayName = "SubscriptionForm";
